import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EntityDetailActions } from 'actions';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import {
    setMarketingConsentAccount,
    setMarketingConsentContact,
} from 'services/MarketingConsentService';
import MarketingStatusCell from './MarketingStatusSelect';
import {
    createSetMarketingConsentWithRowPatch,
    getMarketingStatusRowPatchFields,
} from './patchMarketingStatusRow';

function getConsentSetter(entity) {
    if (entity.entity === COMPANIES.entity) {
        return setMarketingConsentAccount;
    }
    if (entity.entity === CONTACTS.entity) {
        return setMarketingConsentContact;
    }
    throw new Error('MarketingStatusDetailField: unsupported entity');
}

const MarketingStatusDetailField = memo(({ entity, record }) => {
    const dispatch = useDispatch();
    const spamchimp = useSelector((state) => state?.config?.flagsmith?.spamchimp);
    const permission = useSelector((state) => state?.config?.permission);
    const marketingFieldConfig = useSelector(
        (state) =>
            state.config?.standardFieldsSchemaMap?.[entity.extraFieldName]?.marketingStatus ?? null,
    );

    const visible = spamchimp && (marketingFieldConfig == null || !marketingFieldConfig.isHidden);

    const crudPermissions = permission?.crud_permission?.[entity.permission] ?? null;
    const canUpdate = !!crudPermissions?.update;

    const persistMarketingConsent = useMemo(() => {
        const base = createSetMarketingConsentWithRowPatch(getConsentSetter(entity), entity);

        return async (args) => {
            const result = await base(args);
            dispatch(
                EntityDetailActions.updateDetailFields(
                    entity,
                    args.id,
                    getMarketingStatusRowPatchFields(args.marketingStatusId),
                ),
            );
            return result;
        };
    }, [dispatch, entity]);

    const rowData = useMemo(() => {
        if (!record?.id) return null;
        const statusId = record.marketingStatusId ?? record.MarketingStatusId;
        return {
            ...record,
            Id: record.id,
            marketingStatusId: statusId,
            MarketingStatusId: statusId,
        };
    }, [record]);

    const value = rowData?.marketingStatusId ?? rowData?.MarketingStatusId;

    const isReadOnly =
        !canUpdate || getBackendBoolean(record?.isReadOnly) || !!marketingFieldConfig?.readOnly;

    if (!visible || !rowData) {
        return null;
    }

    return (
        <MarketingStatusCell
            data={rowData}
            value={value}
            setMarketingConsent={persistMarketingConsent}
            isReadOnly={isReadOnly}
        />
    );
});

MarketingStatusDetailField.displayName = 'MarketingStatusDetailField';

export default MarketingStatusDetailField;
